import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errText, media } from "../../lib/api";
import { Badge, KeyRow, Panel, Stat, TabBar, eurOf, money, shortDate, stamp, statusTone, useEurRate } from "./ui";
import { Bell, Download, FileText, X } from "lucide-react";

const STATUS_TABS = [
  ["pending_review", "Pending", "warn"],
  ["approved", "Queued", "good"],
  ["processed", "Processed", "ink"],
  ["rejected", "Rejected", "bad"],
];

const SLA_TONE = { fresh: "good", aging: "warn", overdue: "bad" };
const slaLabel = (sla) => {
  if (!sla || sla.status === "ok") return null;
  const h = sla.age_hours || 0;
  const display = h >= 48 ? `${Math.round(h / 24)}d` : `${Math.round(h)}h`;
  if (sla.status === "overdue") return `overdue · ${display}`;
  return `waiting ${display}`;
};

export default function PayoutRequestsPage() {
  const [tab, setTab] = useState("pending_review");
  const [data, setData] = useState({ requests: [], counts: {}, queued_amount_mad: 0 });
  const [msg, setMsg] = useState("");
  const [drawer, setDrawer] = useState(null);
  const [remindersOpen, setRemindersOpen] = useState(false);
  const [reminderPreview, setReminderPreview] = useState(null);
  const fx = useEurRate();

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/admin/payout-requests", { params: { status: tab } });
      setData(d);
    } catch (e) { setMsg(errText(e)); }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const runReminders = async (dry_run) => {
    setMsg("");
    try {
      const { data: d } = await api.post("/admin/payout-requests/reminders/run", null, { params: { dry_run } });
      setReminderPreview(d);
      if (!dry_run) setMsg(`Reminder sent to ${d.sent?.length || 0} shop${(d.sent?.length || 0) === 1 ? "" : "s"}.`);
    } catch (e) { setMsg(errText(e)); }
  };

  const act = async (fn, done = "Saved.") => {
    setMsg("");
    try { await fn(); setMsg(done); await load(); }
    catch (e) { setMsg(errText(e)); }
  };

  const csv = async () => {
    const r = await api.get("/admin/payout-requests/export", { params: { status: tab }, responseType: "blob" });
    const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `payout-requests-${tab}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5" data-testid="admin-payout-requests-page">
      <div className="flex items-end gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl text-ink">Payout requests</h1>
          <p className="text-sm text-muted">Florists submit a signed invoice; you validate it, then process the transfer.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat testid="req-kpi-pending" label="Awaiting review" value={data.counts.pending_review || 0} tone={data.overdue_count ? "bad" : "warn"}
              hint={data.overdue_count ? `${data.overdue_count} overdue · SLA ${data.sla_hours || 48}h` : `SLA ${data.sla_hours || 48}h`} />
        <Stat testid="req-kpi-queued" label="In this week's queue" value={data.counts.approved || 0} hint={`${money(data.queued_amount_mad)} MAD`} />
        <Stat testid="req-kpi-processed" label="Processed" value={data.counts.processed || 0} />
        <Stat testid="req-kpi-rejected" label="Rejected" value={data.counts.rejected || 0} tone="bad" />
        <button className="card p-4 text-start" data-testid="req-reminders" onClick={() => setRemindersOpen(true)}>
          <p className="smallcaps">Monthly reminders</p>
          <p className="font-display text-2xl text-ink flex items-center gap-2"><Bell size={18} /> Preview</p>
          <p className="text-xs text-muted mt-1">Nudge shops with a balance</p>
        </button>
      </div>

      <div>
        <button className="btn-ghost text-xs inline-flex items-center gap-1" data-testid="req-export" onClick={csv}>
          <Download size={12} /> Export {tab} as CSV
        </button>
      </div>

      {msg ? <p className="text-sm text-brass" data-testid="req-message">{msg}</p> : null}

      <TabBar testid="req-tabs" value={tab} onChange={setTab}
              tabs={STATUS_TABS.map(([id, label]) => [id, label, data.counts[id] || 0])} />

      <div className="overflow-x-auto border border-brass-light bg-white">
        <table className="w-full text-sm">
          <thead className="bg-plaster-deep">
            <tr>{["Received", "Shop", "Period", "Orders", "Amount", "Invoice", "Status", "Actions"].map((h) => (
              <th key={h} className="text-start px-3 py-2 smallcaps whitespace-nowrap">{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {data.requests.map((r) => (
              <tr key={r.id} className={`border-t border-brass-light hover:bg-plaster/60 ${r.sla?.status === "overdue" ? "bg-danger/5" : ""}`} data-testid={`req-row-${r.id}`}>
                <td className="px-3 py-2 text-xs whitespace-nowrap">
                  {stamp(r.created_at)}
                  {r.sla && slaLabel(r.sla) ? (
                    <div className="mt-1"><Badge tone={SLA_TONE[r.sla.status]} testid={`req-sla-${r.id}`}>{slaLabel(r.sla)}</Badge></div>
                  ) : null}
                </td>
                <td className="px-3 py-2">
                  <Link to={`/admin/florists/${r.florist_id}`} className="underline decoration-brass underline-offset-2" data-testid={`req-shop-${r.id}`}>
                    {r.shop_name}
                  </Link>
                </td>
                <td className="px-3 py-2 text-xs whitespace-nowrap">{shortDate(r.period_start)} → {shortDate(r.period_end)}</td>
                <td className="px-3 py-2 tabular">{r.orders}</td>
                <td className="px-3 py-2 tabular whitespace-nowrap">{money(r.amount_mad)} MAD <span className="text-brass">{eurOf(fx, r.amount_mad)}</span></td>
                <td className="px-3 py-2">
                  {r.invoice_url ? (
                    <a className="btn-ghost text-[11px] inline-flex items-center gap-1" href={media(r.invoice_url)} target="_blank" rel="noreferrer" data-testid={`req-invoice-${r.id}`}>
                      <FileText size={12} /> Open
                    </a>
                  ) : <span className="text-xs text-muted">—</span>}
                </td>
                <td className="px-3 py-2"><Badge tone={statusTone(r.status)}>{r.status}</Badge></td>
                <td className="px-3 py-2">
                  <button className="btn-ghost text-[11px]" data-testid={`req-open-${r.id}`} onClick={() => setDrawer(r)}>Review</button>
                </td>
              </tr>
            ))}
            {data.requests.length === 0 ? <tr><td colSpan={8} className="p-4 text-sm text-muted">No request in this tab.</td></tr> : null}
          </tbody>
        </table>
      </div>

      {drawer ? <RequestDrawer request={drawer} onClose={() => setDrawer(null)} onDone={() => { setDrawer(null); load(); }} setMsg={setMsg} /> : null}

      {remindersOpen ? (
        <div className="fixed inset-0 scrim z-40 flex items-center justify-center p-6" onClick={() => setRemindersOpen(false)} data-testid="reminders-modal">
          <div className="bg-plaster w-full max-w-lg max-h-[85vh] overflow-y-auto p-6 border border-brass" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 border-b border-brass-light pb-3 mb-4">
              <h2 className="font-display text-2xl text-ink">Monthly payout reminders</h2>
              <button className="ms-auto" onClick={() => setRemindersOpen(false)}><X size={18} /></button>
            </div>
            <p className="text-xs text-muted mb-3">
              Sends an email to every active shop with an unclaimed balance above the threshold, once per month.
              Configure the day and threshold in Admin → Settings.
            </p>
            <div className="flex gap-2 mb-3">
              <button className="btn-ghost text-xs" data-testid="reminders-preview" onClick={() => runReminders(true)}>Preview today</button>
              <button className="btn-primary text-xs" data-testid="reminders-send" onClick={() => { if (window.confirm("Send the reminder emails now?")) runReminders(false); }}>Send now</button>
            </div>
            {reminderPreview ? (
              <div className="text-xs space-y-2" data-testid="reminders-preview-result">
                <p className="smallcaps">Will notify {reminderPreview.sent?.length || 0} shop(s) · skip {reminderPreview.skipped?.length || 0}</p>
                {(reminderPreview.sent || []).map((s) => (
                  <div key={s.florist_id} className="border border-brass-light p-2">
                    <b>{s.shop_name}</b> · {money(s.amount_mad)} MAD · {s.orders} orders · {s.email}
                  </div>
                ))}
                {(reminderPreview.skipped || []).length ? (
                  <details className="mt-2">
                    <summary className="cursor-pointer text-muted">{reminderPreview.skipped.length} skipped</summary>
                    {reminderPreview.skipped.map((s) => (
                      <p key={`${s.florist_id}-${s.reason}`} className="text-muted">— {s.shop_name}: {s.reason}</p>
                    ))}
                  </details>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function RequestDrawer({ request, onClose, onDone, setMsg }) {
  const [reason, setReason] = useState("");
  const [reference, setReference] = useState(`VIR-${Date.now()}`);
  const openStatement = async () => {
    const r = await api.get(`/admin/payout-requests/${request.id}/statement`, { responseType: "blob" });
    window.open(URL.createObjectURL(new Blob([r.data], { type: "text/html" })), "_blank");
  };
  const call = async (fn, done) => {
    try { await fn(); setMsg(done); onDone(); }
    catch (e) { setMsg(errText(e)); }
  };
  const approve = () => call(() => api.post(`/admin/payout-requests/${request.id}/approve`), "Approved.");
  const reject = () => {
    if (reason.trim().length < 3) { setMsg("Please explain why so the florist can fix it."); return; }
    call(() => api.post(`/admin/payout-requests/${request.id}/reject`, { reason }), "Rejected.");
  };
  const process = () => {
    if (!reference.trim()) { setMsg("Add a transfer reference."); return; }
    call(() => api.post(`/admin/payout-requests/${request.id}/process`, { transfer_reference: reference }), "Processed — florist notified.");
  };

  return (
    <div className="fixed inset-0 scrim z-40 flex justify-end" onClick={onClose} data-testid="req-drawer">
      <div className="bg-plaster w-full max-w-xl h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-ink text-plaster px-6 py-4 flex items-center gap-3 z-10">
          <div>
            <h2 className="font-display text-2xl">{request.shop_name}</h2>
            <p className="text-xs text-plaster/70">{money(request.amount_mad)} MAD · {request.orders} order{request.orders === 1 ? "" : "s"}</p>
          </div>
          <button onClick={onClose} className="ms-auto" data-testid="req-drawer-close"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-4">
          <Panel title="Request details" testid="req-details">
            <KeyRow label="Period">{shortDate(request.period_start)} → {shortDate(request.period_end)}</KeyRow>
            <KeyRow label="Received">{stamp(request.created_at)}</KeyRow>
            <KeyRow label="Status"><Badge tone={statusTone(request.status)}>{request.status}</Badge></KeyRow>
            {request.rejection_reason ? <KeyRow label="Rejection">{request.rejection_reason}</KeyRow> : null}
            {request.transfer_reference ? <KeyRow label="Transfer ref">{request.transfer_reference}</KeyRow> : null}
            {request.reviewed_by_email ? <KeyRow label="Reviewed by">{request.reviewed_by_email} · {stamp(request.reviewed_at)}</KeyRow> : null}
            {request.processed_by_email ? <KeyRow label="Processed by">{request.processed_by_email} · {stamp(request.processed_at)}</KeyRow> : null}
            {request.notes ? <KeyRow label="Note">{request.notes}</KeyRow> : null}
          </Panel>

          <Panel title="Orders covered" testid="req-orders">
            <div className="flex flex-wrap gap-2 text-xs">
              {(request.order_numbers || []).map((n) => (
                <Link key={n} to={`/admin/orders?q=${n}`} className="border border-brass-light px-2 py-1 hover:bg-plaster-deep">{n}</Link>
              ))}
            </div>
          </Panel>

          <Panel title="Documents" testid="req-documents">
            <div className="flex flex-wrap gap-2">
              {request.invoice_url ? (
                <a className="btn-ghost text-xs inline-flex items-center gap-1" href={media(request.invoice_url)} target="_blank" rel="noreferrer" data-testid="req-open-invoice">
                  <FileText size={13} /> Florist's signed invoice
                </a>
              ) : null}
              {["approved", "processed"].includes(request.status) ? (
                <button className="btn-ghost text-xs inline-flex items-center gap-1" data-testid="req-open-statement" onClick={openStatement}>
                  <FileText size={13} /> Fleurinelle statement (PDF)
                </button>
              ) : null}
            </div>
          </Panel>

          {request.status === "pending_review" ? (
            <Panel title="Decision" testid="req-decision">
              <div className="flex gap-2">
                <button className="btn-primary flex-1" data-testid="req-approve" onClick={approve}>Approve invoice</button>
              </div>
              <div className="border-t border-brass-light pt-3">
                <label className="block text-xs mb-1">Rejection reason (visible to the florist)</label>
                <textarea className="field min-h-[80px]" data-testid="req-reason" value={reason} onChange={(e) => setReason(e.target.value)}
                          placeholder="e.g. Total mismatch, VAT missing, signature not visible…" />
                <button className="btn-ghost text-xs mt-2 text-danger" data-testid="req-reject" onClick={reject}>Reject with reason</button>
              </div>
            </Panel>
          ) : null}

          {request.status === "approved" ? (
            <Panel title="Send the transfer" testid="req-process">
              <label className="block text-xs mb-1">Transfer reference</label>
              <input className="field" data-testid="req-reference" value={reference} onChange={(e) => setReference(e.target.value)} />
              <p className="text-xs text-muted mt-2">Marks the request paid, adds it to this week's payouts, and notifies the florist.</p>
              <button className="btn-primary w-full mt-2" data-testid="req-mark-processed" onClick={process}>Mark processed & notify shop</button>
            </Panel>
          ) : null}
        </div>
      </div>
    </div>
  );
}
